// src/components/PropertyView.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPropertyById } from '../api';

function PropertyView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const userRole = localStorage.getItem('role');

    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchProperty = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getPropertyById(id);
                setProperty(data);
            } catch (err) {
                setError(err.response?.data?.error || err.message || 'Failed to load property');
            } finally {
                setLoading(false);
            }
        };
        fetchProperty();
    }, [id]);

    const canEdit = () => {
        if (!property) return false;
        if (userRole === 'admin') return true;
        if (userRole === 'team_lead') return true; // city check happens on backend
        return false; // rep/broker edit their own — checked on backend
    };

    const formatValue = (value, fallback = 'N/A') => {
        if (value === undefined || value === null || value === '' || value === 'N/A') return fallback;
        if (typeof value === 'boolean') return value ? '✅ Yes' : '❌ No';
        return value;
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
            approved: 'bg-green-100 text-green-800 border-green-300',
            rejected: 'bg-red-100 text-red-800 border-red-300',
            changes_requested: 'bg-orange-100 text-orange-800 border-orange-300',
        };
        return styles[status] || 'bg-gray-100 text-gray-800 border-gray-300';
    };

    const getLifecycleBadge = (lifecycle) => {
        const styles = {
            active: 'bg-blue-100 text-blue-800 border-blue-300',
            leased: 'bg-purple-100 text-purple-800 border-purple-300',
            sold: 'bg-pink-100 text-pink-800 border-pink-300',
            withdrawn: 'bg-gray-100 text-gray-800 border-gray-300',
        };
        return styles[lifecycle] || 'bg-gray-100 text-gray-800 border-gray-300';
    };

    const timeAgo = (dateString) => {
        if (!dateString) return 'N/A';
        const d = new Date(dateString);
        const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
        if (days === 0) return 'today';
        if (days === 1) return 'yesterday';
        if (days < 30) return `${days} days ago`;
        return d.toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading property...</p>
                </div>
            </div>
        );
    }

    if (error || !property) {
        return (
            <div className="p-4 md:p-6 max-w-4xl mx-auto">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    ❌ {error || 'Property not found'}
                </div>
                <button
                    onClick={() => navigate(-1)}
                    className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
                >
                    ← Back
                </button>
            </div>
        );
    }

    const isOffice = ['Office', 'Office Lease', 'Office Sale'].includes(property.property_type);
    const isWarehouse = ['Warehouse Rent', 'Warehouse Sale', 'Industrial Land', 'Factory', 'Godown', 'Cold Storage', 'Logistics Park'].includes(property.property_type);

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            {/* Back button */}
            <button
                onClick={() => navigate(-1)}
                className="text-teal-600 hover:text-teal-800 mb-4 text-sm"
            >
                ← Back
            </button>

            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
                    <div className="flex-1">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                            🏢 {property.location || 'Untitled Property'}
                        </h1>
                        <p className="text-gray-600">
                            📍 {property.sub_locality || 'N/A'}, {property.city || 'N/A'}
                        </p>

                        {/* Badges */}
                        <div className="flex flex-wrap gap-2 mt-3">
                            <span className={`text-xs px-3 py-1 rounded-full border ${getStatusBadge(property.status)}`}>
                                Status: {property.status}
                            </span>
                            <span className={`text-xs px-3 py-1 rounded-full border ${getLifecycleBadge(property.lifecycle)}`}>
                                Lifecycle: {property.lifecycle || 'active'}
                            </span>
                            <span className="text-xs px-3 py-1 rounded-full border bg-blue-50 text-blue-800 border-blue-200">
                                {property.property_type}
                            </span>
                            {property.is_treta_exclusive && (
                                <span className="text-xs px-3 py-1 rounded-full border bg-purple-100 text-purple-800 border-purple-300">
                                    ⭐ Treta Exclusive
                                </span>
                            )}
                        </div>

                        {/* Changes requested notice */}
                        {property.status === 'changes_requested' && property.changesRequestComment && (
                            <div className="bg-orange-50 border border-orange-200 rounded p-3 mt-3 text-sm">
                                <span className="font-bold text-orange-700">📝 Changes requested:</span>{' '}
                                {property.changesRequestComment}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => navigate(`/properties?focus=${property._id}`)}
                            className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded text-sm"
                        >
                            📍 View on Map
                        </button>
                        {canEdit() && (
                            <button
                                onClick={() => navigate(`/edit-property/${property._id}`)}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
                            >
                                ✏️ Edit
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Grid: Area + Commercial */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white rounded-lg shadow-sm p-5">
                    <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">📐 Area & Physical</h3>
                    <dl className="space-y-2 text-sm">
                        <Row label="Carpet Area" value={property.carpet_area} />
                        <Row label="Chargeable Area" value={property.chargeable_area} />
                        <Row label="Leasable Area" value={property.leasable_area} />
                        <Row label="Carpet Efficiency" value={property.carpet_efficiency ? `${property.carpet_efficiency}%` : null} />
                        <Row label="Floor" value={property.floor} />
                        <Row label="Floor to Ceiling" value={property.floor_to_ceiling_height} />
                        <Row label="Floor to Floor" value={property.floor_to_floor_height} />
                        <Row label="Interior Condition" value={property.interior_condition || property.furnishing} />
                    </dl>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-5">
                    <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">💰 Commercial Terms</h3>
                    <dl className="space-y-2 text-sm">
                        <Row label="Price" value={property.price} />
                        <Row label="Rent per Sq.Ft." value={property.rent_per_sqft} />
                        <Row label="Price per Sq.Ft." value={property.price_per_sqft} />
                        <Row label="CAM Charges" value={property.cam_charges} />
                        <Row label="Security Deposit" value={property.security_deposit} />
                        <Row label="Rent Free Period" value={property.rent_free_period} />
                        <Row label="Rent Escalation" value={property.rent_escalation} />
                        <Row label="Lock-in Period" value={property.lock_in_period} />
                        <Row label="Notice Period" value={property.notice_period} />
                    </dl>
                </div>
            </div>

            {/* Parking + Amenities */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white rounded-lg shadow-sm p-5">
                    <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">🅿️ Parking</h3>
                    <dl className="space-y-2 text-sm">
                        <Row label="Car Parks" value={property.car_parks} />
                        <Row label="Parking Details" value={property.parking_details} />
                        <Row label="Additional Parking Cost" value={property.additional_parking_cost} />
                    </dl>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-5">
                    <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">📍 Location Amenities</h3>
                    <dl className="space-y-2 text-sm">
                        <Row label="Nearest Metro" value={property.nearest_metro} />
                        <Row label="Airport T-1" value={property.distance_airport_t1} />
                        <Row label="Airport T-2" value={property.distance_airport_t2} />
                        <Row label="Nearest Hospital" value={property.nearest_hospital} />
                        <Row label="Nearest Police" value={property.nearest_police_station} />
                        <Row label="Nearest Fire" value={property.nearest_fire_station} />
                    </dl>
                </div>
            </div>

            {/* Legal */}
            <div className="bg-white rounded-lg shadow-sm p-5 mb-4">
                <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">⚖️ Legal & Registration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <Row label="Stamp Duty" value={property.stamp_duty} />
                    <Row label="Registration Details" value={property.registration_details} />
                    <Row label="Occupancy Certificate" value={property.occupancy_certificate} />
                    <Row label="Handover Date" value={property.handover_date} />
                    <Row label="Property Age" value={property.property_age} />
                </div>
            </div>

            {/* Warehouse specific (if applicable) */}
            {isWarehouse && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 mb-4">
                    <h3 className="font-bold text-orange-800 mb-3 pb-2 border-b border-orange-300">🏭 Warehouse Specific</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                        <Row label="Plot Area" value={property.plot_area} />
                        <Row label="Built-up Area" value={property.built_up_area} />
                        <Row label="Warehouse Area" value={property.warehouse_area} />
                        <Row label="Ceiling Height" value={property.ceiling_height} />
                        <Row label="Floor Load Capacity" value={property.floor_load_capacity} />
                        <Row label="Number of Docks" value={property.number_of_docks} />
                        <Row label="Dock Type" value={property.dock_type} />
                        <Row label="Power Supply" value={property.power_supply} />
                        <Row label="Water Supply" value={property.water_supply} />
                        <Row label="Fire Safety" value={property.fire_safety} />
                        <Row label="Security System" value={property.security_system} />
                        <Row label="Road Access" value={property.road_access} />
                        <Row label="Truck Parking" value={property.truck_parking} />
                        <Row label="Proximity to Highway" value={property.proximity_to_highway} />
                        <Row label="Air Conditioned" value={property.is_air_conditioned} />
                        <Row label="Crane Facility" value={property.has_crane_facility} />
                        <Row label="Weighbridge" value={property.has_weighbridge} />
                    </div>
                </div>
            )}

            {/* Special conditions */}
            <div className="bg-white rounded-lg shadow-sm p-5 mb-4">
                <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">🔧 Special Conditions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <Row label="Tenant Fitout Allowed" value={property.tenant_fitout_allowed} />
                    <Row label="GST Applicable" value={property.gst_applicable} />
                    <Row label="GST %" value={property.gst_percentage ? `${property.gst_percentage}%` : null} />
                    <Row label="Pre-Leased" value={property.pre_leased} />
                </div>
            </div>

            {/* Highlights & Description */}
            {(property.highlights || property.description) && (
                <div className="bg-white rounded-lg shadow-sm p-5 mb-4">
                    <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">📝 Description</h3>
                    {property.highlights && property.highlights !== 'N/A' && (
                        <div className="mb-3">
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Highlights</div>
                            <p className="text-sm text-gray-700">{property.highlights}</p>
                        </div>
                    )}
                    {property.description && property.description !== 'N/A' && (
                        <div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Description</div>
                            <p className="text-sm text-gray-700">{property.description}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Landlord */}
            <div className="bg-white rounded-lg shadow-sm p-5 mb-4">
                <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">👤 Landlord</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <Row label="Landlord Name" value={property.landlord_name} />
                    <Row label="Landlord Contact" value={property.landlord_contact} />
                </div>
            </div>

            {/* Audit Trail */}
            <div className="bg-gray-50 rounded-lg p-5 mb-4 text-xs text-gray-600">
                <h3 className="font-bold text-gray-700 mb-3">📋 Audit Trail</h3>
                <div className="space-y-1">
                    <div>Property ID: <span className="font-mono">{property.propertyID || property._id}</span></div>
                    <div>Created: {property.created_at ? new Date(property.created_at).toLocaleString() : 'N/A'}
                        {property.createdByEmail && ` by ${property.createdByEmail}`}
                    </div>
                    {property.approvedAt && (
                        <div>Approved: {new Date(property.approvedAt).toLocaleString()}</div>
                    )}
                    {property.lastEditedAt && (
                        <div>Last edited: {new Date(property.lastEditedAt).toLocaleString()}
                            {property.lastEditedBy && ` by ${property.lastEditedBy}`}
                        </div>
                    )}
                    {property.deactivatedAt && (
                        <div>Deactivated: {new Date(property.deactivatedAt).toLocaleString()}
                            {property.deactivationReason && ` (${property.deactivationReason})`}
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom actions */}

            <div className="flex flex-wrap gap-3">
                <button
                    onClick={() => navigate(-1)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-2 rounded font-medium"
                >
                    ← Back
                </button>
                <button
                    onClick={() => navigate(`/properties?focus=${property._id}`)}
                    className="bg-teal-500 hover:bg-teal-600 text-white px-5 py-2 rounded font-medium"
                >
                    📍 View on Map
                </button>
                {canEdit() && (
                    <button
                        onClick={() => navigate(`/edit-property/${property._id}`)}
                        className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2 rounded font-medium"
                    >
                        ✏️ Edit Property
                    </button>
                )}
            </div>
        </div>
    );
}

// Helper sub-component for row display
function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-3 py-1 border-b border-gray-100 last:border-0">
            <dt className="text-gray-500 flex-shrink-0">{label}</dt>
            <dd className="text-gray-800 font-medium text-right">
                {value === undefined || value === null || value === '' || value === 'N/A'
                    ? <span className="text-gray-300">—</span>
                    : typeof value === 'boolean'
                        ? (value ? '✅ Yes' : '❌ No')
                        : value}
            </dd>
        </div>
    );
}

export default PropertyView;